import { type ReactElement, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@/components/ui';
import { Shell } from '../../components/shell/Shell.tsx';
import {
  createCompany,
  type CreateCompanyInput,
} from '../../state/companies/api.ts';

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'company name must be at least 2 characters'),
  invited_emails: z.string().default(''),
});

const emailSchema = z.string().email();

type FormValues = z.input<typeof schema>;

function parseInvitedEmails(raw: string): string[] {
  const items = raw
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return Array.from(new Set(items));
}

export function CompanyCreatePage(): ReactElement {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [lastPayload, setLastPayload] = useState<CreateCompanyInput | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      invited_emails: '',
    },
  });

  async function submitPayload(payload: CreateCompanyInput): Promise<void> {
    setServerError(null);
    clearErrors('name');

    const response = await createCompany(payload);
    if (response.ok) {
      navigate(`/company/${response.data.company_id}/dashboard`, { replace: true });
      return;
    }

    if (response.error.field === 'name') {
      setError('name', { type: 'server', message: response.error.message });
      return;
    }

    setServerError(response.error.message || 'Unable to create company. Please try again.');
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    clearErrors('invited_emails');

    const invitedEmails = parseInvitedEmails(values.invited_emails ?? '');
    for (const email of invitedEmails) {
      if (!emailSchema.safeParse(email).success) {
        setError('invited_emails', {
          type: 'validate',
          message: `invalid email: ${email}`,
        });
        return;
      }
    }

    const payload: CreateCompanyInput = {
      name: values.name.trim(),
      invited_emails: invitedEmails,
    };

    setLastPayload(payload);
    await submitPayload(payload);
  });

  async function handleRetry(): Promise<void> {
    if (!lastPayload) {
      return;
    }
    setIsRetrying(true);
    try {
      await submitPayload(lastPayload);
    } finally {
      setIsRetrying(false);
    }
  }

  return (
    <Shell mode="individual" densityMode="editorial" items={[
        { label: 'My dashboard', to: '/dashboard' },
        { label: 'Create company', to: null },
      ]}
    >
      <div className="max-w-2xl">
        <p className="font-mono text-mono text-muted-2">CREATE COMPANY</p>
        <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
          Create company workspace
        </h1>
        <p className="mt-4 text-muted">
          Set up your company workspace and optionally add teammate emails to invite
          after creation.
        </p>

        <form className="mt-6 flex flex-col gap-5" onSubmit={onSubmit} noValidate>
          <Input label="Company name" id="company-name" error={errors.name?.message} type="text" {...register('name')} />

          <Input
            label="Invited emails (optional)"
            id="invited-emails"
            helperText="Separate multiple emails with commas or new lines."
            error={errors.invited_emails?.message}
          />
          <textarea
            id="invited-emails"
            rows={4}
            {...register('invited_emails')}
            className="w-full rounded-md border border-line-2 bg-card px-3.5 py-2.5 font-sans text-body text-ink placeholder:text-muted-2 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
          />

          {serverError ? (
            <div
              role="alert"
              className="rounded-lg border border-line bg-white p-4 text-error"
            >
              <p className="font-medium">{serverError}</p>
              <div className="mt-3 flex items-center gap-3">
                <Button variant="secondary" type="button" disabled={isRetrying} onClick={() => void handleRetry()}>
                  {isRetrying ? 'Retrying…' : 'Retry'}
                </Button>
                <Link
                  to="/dashboard"
                  className="text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  Back to dashboard
                </Link>
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating company…' : 'Create company'}
            </Button>
            <Link
              to="/dashboard"
              className="text-sm text-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </Shell>
  );
}
